// media.service.ts (o dentro de image-fallback.service.ts)
import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class ImageFallbackService {
  private readonly storageBase = environment.storageUrl
    ? environment.storageUrl.replace(/\/+$/, '')
    : '';

  private platformId = inject(PLATFORM_ID);

  getConstructedUrl(
    path: string | null | undefined,
    type: 'profile' | 'project' | 'item' | 'banner' | 'emoji',
  ): string {
    if (!path) return this.getFallback(type);
    if (
      path.startsWith('http') ||
      path.startsWith('assets/') ||
      path.startsWith('data:') ||
      path.startsWith('blob:')
    ) {
      // Optimización automática de Cloudinary a WebP/AVIF
      // Items, perfil y banner: q_90 para usuarios PRO, q_auto para el resto
      if (path.includes('res.cloudinary.com') && !path.includes('/upload/f_')) {
        const quality =
          (type === 'item' || type === 'profile' || type === 'banner') &&
          this.isProUser()
            ? 'q_90'
            : 'q_auto';
        return path.replace('/upload/', `/upload/f_auto,${quality}/`);
      }
      return path;
    }

    // Remover slash inicial y "storage/" de la rutaza si están presentes
    let cleanPath = path.replace(/^\/+/, '').replace(/^storage\//, '');

    const prefixMap: Record<string, string> = {
      profile: 'profiles/',
      project: 'miniaturas/',
      item: 'items/',
      banner: 'profiles/',
      emoji: 'emojis/',
    };

    const prefix = prefixMap[type];

    // Asegurar el prefijo de carpeta según el tipo si no lo tiene
    if (prefix && !cleanPath.startsWith(prefix)) {
      cleanPath = `${prefix}${cleanPath}`;
    }

    // Construir URL absoluta apuntando al Backend
    return `${this.storageBase}/${cleanPath}`;
  }

  private isProUser(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    try {
      const stored = localStorage.getItem('user_profile');
      if (!stored) return false;
      const profile = JSON.parse(stored);
      return profile?.is_pro ?? false;
    } catch {
      return false;
    }
  }

  public getFallback(type: string): string {
    const paths: Record<string, string> = {
      profile: '/perfil/user.svg',
      project: '/project/project.svg',
      item: '/project/item.svg',
      banner: '/perfil/banner.svg',
      emoji: '/perfil/emoji.svg',
    };
    return paths[type] || paths['project'];
  }

  handleImageError(
    event: Event,
    type: 'banner' | 'profile' | 'project' | 'item' | 'emoji',
  ): void {
    const imgElement = event.target as HTMLImageElement;

    // Si ya intentamos cargar el fallback y vuelve a fallar, paramos el bucle.
    if (imgElement.dataset['fallbackAttempted']) {
      return;
    }

    imgElement.dataset['fallbackAttempted'] = 'true';
    imgElement.src = this.getFallback(type);
  }

  getFallbackImage(
    type: 'profile' | 'project' | 'item' | 'banner' | string,
  ): string {
    return this.getFallback(type);
  }
}
